/**
 * Procedural HEX map generation — deterministic given the seed.
 *
 * The hex grid itself (biomes + heights) is produced by generateHexGrid
 * (sim/hex.ts, shared with the 3D demo). This module's job is to turn that
 * grid into LIVE sim state: block impassable biomes for pathing and scatter
 * resource-node entities by biome.
 *
 * Buildings/players don't live here — match.ts picks start hexes and spawns
 * starting entities. Pure sim, no DOM.
 */
import type { World } from "./world";
import type { Registry } from "../data/registry";
import type { TerrainRecipe, TerrainType } from "../data/schema";
import { axialToOffset, generateHexGrid, HEX_DIRECTIONS, mulberry32, offsetToAxial, tierHeight, type Biome, type Hex } from "./hex";
import { spawnResourceNode, spawnAnimal, spawnFish, type Species } from "./spawn";

export interface HexGenOptions {
  cols?: number;
  rows?: number;
  size?: number;
  seed?: number;
}

/** Map-data terrain names deliberately map to the simulation's concrete biomes. */
export const TERRAIN_BIOMES: Record<TerrainType, Biome> = {
  grass: "grass",
  forest_floor: "forest",
  water: "water",
  rock: "rock",
  sand: "sand",
};

/** Recipe extension used while placing starts: protected cells finish as grass. */
export type TerrainRecipeOptions = TerrainRecipe & {
  protectedHexes?: readonly { col: number; row: number }[];
};

function paintBiome(hex: Hex, biome: Biome, size: number): void {
  hex.biome = biome;
  if (biome === "water" || biome === "deepWater") {
    hex.level = -1;
    hex.height = -0.2;
  } else {
    hex.level = biome === "rock" ? 1 : 0;
    hex.height = 0.18 + hex.level * tierHeight(size);
  }
}

/** Overlay deterministic, authored biome blobs over the generated grid. */
export function applyTerrainRecipe(hexes: Hex[], recipe: TerrainRecipeOptions, seed: number, size: number): void {
  if (recipe.base) {
    const biome = TERRAIN_BIOMES[recipe.base];
    for (const hex of hexes) paintBiome(hex, biome, size);
  }
  const byKey = new Map(hexes.map((hex) => [`${hex.col},${hex.row}`, hex]));
  const protectedHexes = new Set((recipe.protectedHexes ?? []).map(({ col, row }) => `${col},${row}`));
  const claimed = new Set<string>();
  const rng = mulberry32(seed ^ 0x51ed270b);
  for (const feature of recipe.features ?? []) {
    const biome = TERRAIN_BIOMES[feature.type];
    for (let cluster = 0; cluster < feature.count; cluster++) {
      const candidates = hexes.filter((hex) => {
        const key = `${hex.col},${hex.row}`;
        return !claimed.has(key) && !protectedHexes.has(key);
      });
      if (!candidates.length) break;
      const target = feature.minSize + Math.floor(rng() * (feature.maxSize - feature.minSize + 1));
      const start = candidates[Math.floor(rng() * candidates.length)];
      const queue = [start];
      const seen = new Set<string>([`${start.col},${start.row}`]);
      let painted = 0;
      while (queue.length && painted < target) {
        const hex = queue.shift()!;
        const hexKey = `${hex.col},${hex.row}`;
        if (claimed.has(hexKey) || protectedHexes.has(hexKey)) continue;
        paintBiome(hex, biome, size);
        claimed.add(hexKey);
        painted++;
        const [q, r] = offsetToAxial(hex.col, hex.row);
        for (const [dq, dr] of HEX_DIRECTIONS) {
          const [col, row] = axialToOffset(q + dq, r + dr);
          const key = `${col},${row}`;
          const neighbor = byKey.get(key);
          if (neighbor && !claimed.has(key) && !protectedHexes.has(key) && !seen.has(key)) {
            seen.add(key);
            queue.push(neighbor);
          }
        }
      }
    }
  }
  for (const key of protectedHexes) {
    const hex = byKey.get(key);
    if (hex) paintBiome(hex, "grass", size);
  }
}

/** Biomes a unit cannot enter / a building cannot be placed on. */
const BLOCKED_BIOMES = new Set(["deepWater", "water", "mountain"]);

export interface MapBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** Bounds are invariant for a generated World. Calculate them once per pass,
 * not once per candidate hex. */
export function mapBounds(world: Pick<World, "hexes">): MapBounds {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const h of world.hexes) {
    if (h.x < minX) minX = h.x;
    if (h.x > maxX) maxX = h.x;
    if (h.z < minZ) minZ = h.z;
    if (h.z > maxZ) maxZ = h.z;
  }
  return { minX, maxX, minZ, maxZ };
}

/** Min distance from a precomputed map rectangle edge (world units). */
export function edgeDistance({ minX, maxX, minZ, maxZ }: MapBounds, h: { x: number; z: number }): number {
  return Math.min(h.x - minX, maxX - h.x, h.z - minZ, maxZ - h.z);
}

/**
 * Set world.blocked for terrain that fully OCCUPIES a hex: water (can't walk on
 * the sea) and the faded vignette border band (off-map). Elevation does NOT
 * block tiles here — hills/mountains are walkable land; whether you can climb a
 * given face is decided per-EDGE in pathfinding (World.canClimb / MAX_CLIMB),
 * so a too-steep cliff is routed around instead of being an invisible tile wall.
 */
export function blockTerrain(world: World): void {
  // Only WATER blocks. Every land tile — right out to the coast — is walkable and
  // buildable now that the map edge renders as a real island cliff (no dark
  // off-map vignette). The old border band made the outer ~2 tiles a dead zone
  // where units got stuck attacking edge buildings; that block is gone.
  for (const h of world.hexes) {
    if (h.biome === "deepWater" || h.biome === "water") world.setBlocked(h.col, h.row, true);
  }
}

/**
 * Block impassable terrain and scatter resource nodes by biome.
 * Mutates world.blocked + spawns resource entities. Deterministic via world.rng.
 * Interior-only (skips the dark vignette edge). `exclude` keeps player start
 * hexes + their neighbor ring clear of resources.
 */
export function populateWorld(
  world: World,
  reg: Registry,
  exclude?: Set<number>,
  proceduralResources = true,
): void {
  const { rng } = world;
  const interiorLimit = world.size * 2.5;
  const skip = exclude ?? new Set<number>();
  const bounds = mapBounds(world);

  // Per-biome spawn probabilities PORTED from the 3D demo's decoration
  // builders (src/demo/hexmap3d.ts) so the live map is dressed the same way
  // the standalone demo is. One resource node per hex, priority order
  // gold > stone > berry > tree (first hit wins).
  const GOLD: Partial<Record<Biome, number>> = { rock: 0.07, mountain: 0.06, snow: 0.05, sand: 0.01 };
  const STONE: Partial<Record<Biome, number>> = { mountain: 0.22, rock: 0.2, snow: 0.16, sand: 0.06 };
  const BERRY: Partial<Record<Biome, number>> = { grass: 0.1, forest: 0.05, savanna: 0.06, taiga: 0.04, jungle: 0.06, sand: 0.02 };
  const TREE: Partial<Record<Biome, number>> = {
    jungle: 0.95, forest: 0.9, taiga: 0.82, snow: 0.18, rock: 0.05,
    grass: 0.08, savanna: 0.07, desert: 0.02, mountain: 0.05, sand: 0.03,
  };

  blockTerrain(world);

  if (proceduralResources) {
    for (const h of world.hexes) {
      if (BLOCKED_BIOMES.has(h.biome)) continue;
      if (!world.isWalkable(h.col, h.row)) continue;
      if (skip.has(world.hkey(h.col, h.row))) continue;
      if (edgeDistance(bounds, h) < interiorLimit) continue;

      let node: string | null = null;
      const b = h.biome;
      if ((GOLD[b] ?? 0) > 0 && rng.next() < (GOLD[b] as number)) node = "gold_ore";
      else if ((STONE[b] ?? 0) > 0 && rng.next() < (STONE[b] as number)) node = "stone_rock";
      else if ((BERRY[b] ?? 0) > 0 && rng.next() < (BERRY[b] as number)) node = "berry_bush";
      else if ((TREE[b] ?? 0) > 0 && rng.next() < (TREE[b] as number)) node = "tree";

      if (node) spawnResourceNode(world, reg, node, { x: h.col, y: h.row });
    }
  }

  // Scatter wandering wildlife across the wild biomes (huntable for food). Only
  // on free interior land (isWalkable already excludes water/resources), away
  // from player starts.
  const FAUNA: Partial<Record<Biome, [Species, number][]>> = {
    grass: [["rabbit", 0.018], ["deer", 0.012]],
    savanna: [["deer", 0.018], ["boar", 0.007]],
    forest: [["deer", 0.02], ["boar", 0.012], ["rabbit", 0.009]],
    taiga: [["deer", 0.018], ["boar", 0.009]],
    jungle: [["boar", 0.018], ["deer", 0.01]],
  };
  for (const h of world.hexes) {
    if (!world.isWalkable(h.col, h.row)) continue;
    if (skip.has(world.hkey(h.col, h.row))) continue;
    if (edgeDistance(bounds, h) < interiorLimit) continue;
    const opts = FAUNA[h.biome];
    if (!opts) continue;
    for (const [sp, p] of opts) {
      if (rng.next() >= p) continue;
      // spawn a small HERD so you actually run into them: the trigger animal plus
      // a few mates on nearby walkable hexes (boars roam more solitary).
      const herd = sp === "boar" ? 1 + Math.floor(rng.next() * 2) : 2 + Math.floor(rng.next() * 4);
      let placed = 0;
      spawnAnimal(world, sp, world.hexCenter(h.col, h.row)); placed++;
      for (const [nc, nr] of world.neighbors(h.col, h.row)) {
        if (placed >= herd) break;
        if (!world.isWalkable(nc, nr) || skip.has(world.hkey(nc, nr))) continue;
        spawnAnimal(world, sp, world.hexCenter(nc, nr)); placed++;
      }
      break;
    }
  }

  // Fish schools cluster along the COAST (shallow water touching land), so a dock
  // built on the shore has something to fish. APPENDED last — after the land-node
  // and fauna loops — so it adds no rng.next() call before them and the existing
  // scatter stays bit-identical (a desync the determinism test would catch).
  if (proceduralResources) {
    for (const h of world.hexes) {
      if (!world.isWater(h.col, h.row)) continue;
      if (edgeDistance(bounds, h) < interiorLimit) continue; // keep off the far cloud edge
      const onCoast = world.isWaterEdge(h.col, h.row);
      const nearCoast = onCoast || world.neighbors(h.col, h.row).some(([nc, nr]) => world.isWaterEdge(nc, nr));
      if (!nearCoast) continue;
      if (rng.next() < (onCoast ? 0.14 : 0.06)) spawnFish(world, reg, "fish_school", { x: h.col, y: h.row });
    }
  }
}

/**
 * Pick `n` far-apart buildable INTERIOR hexes via farthest-point sampling
 * (first sample = closest candidate to the land centroid). Interior constraint
 * keeps starts out of the dark vignette edge.
 */
export function findStarts(
  world: World,
  n: number
): { col: number; row: number }[] {
  const interiorLimit = world.size * 3;
  const bounds = mapBounds(world);
  // Restrict starts to the LARGEST connected passable landmass — otherwise
  // farthest-point sampling happily drops the second player on an isolated
  // island/corner that's cut off from the first by water or mountains.
  const land = largestLandmass(world);
  const cand = world.hexes.filter(
    (h) =>
      land.has(world.hkey(h.col, h.row)) &&
      edgeDistance(bounds, h) >= interiorLimit
  );
  if (cand.length === 0 || n <= 0) return [];

  let cx = 0;
  let cz = 0;
  for (const h of cand) {
    cx += h.x;
    cz += h.z;
  }
  cx /= cand.length;
  cz /= cand.length;

  const chosen: { col: number; row: number; x: number; z: number }[] = [];
  let first = cand[0];
  let fd = Infinity;
  for (const h of cand) {
    const d = (h.x - cx) ** 2 + (h.z - cz) ** 2;
    if (d < fd) {
      fd = d;
      first = h;
    }
  }
  chosen.push(first);

  while (chosen.length < n && chosen.length < cand.length) {
    let best = cand[0];
    let bestMin = -1;
    for (const h of cand) {
      let mn = Infinity;
      for (const c of chosen) {
        const d = (h.x - c.x) ** 2 + (h.z - c.z) ** 2;
        if (d < mn) mn = d;
      }
      if (mn > bestMin) {
        bestMin = mn;
        best = h;
      }
    }
    chosen.push(best);
  }

  return chosen.map((c) => ({ col: c.col, row: c.row }));
}

/** Flood-fill the passable (non-water/mountain) hexes and return the hkey set
 *  of the single largest connected component — the main landmass. */
function largestLandmass(world: World): Set<number> {
  const passable = (col: number, row: number): boolean => {
    const h = world.hexOf(col, row);
    return !!h && !BLOCKED_BIOMES.has(h.biome);
  };
  const seen = new Uint8Array(world.cols * world.rows);
  let best: number[] = [];
  for (const h of world.hexes) {
    const startKey = world.hkey(h.col, h.row);
    if (seen[startKey] || !passable(h.col, h.row)) continue;
    const comp: number[] = [];
    const stack: [number, number][] = [[h.col, h.row]];
    seen[startKey] = 1;
    while (stack.length) {
      const [c, r] = stack.pop()!;
      comp.push(world.hkey(c, r));
      for (const [nc, nr] of world.neighbors(c, r)) {
        const k = world.hkey(nc, nr);
        if (!seen[k] && passable(nc, nr)) {
          seen[k] = 1;
          stack.push([nc, nr]);
        }
      }
    }
    if (comp.length > best.length) best = comp;
  }
  return new Set(best);
}

export { generateHexGrid };
